import { formatDateNumber } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import {
  ExtendedMedicationRequest,
  type MedicationGroup,
} from '@seen/core/models/medicationRequest'
import { isOfType } from '@seen/core/utils'
import { Badge, type BadgeProps, Button, TypographyPageTitle } from '@seen/ui'
import { IconAlertTriangle } from '@tabler/icons-react'
import { orderBy, uniq } from 'lodash-es'
import type { JSX } from 'react'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import { Activity } from '#src/components/activity/Activity.tsx'
import { Link, Loader } from '#src/components/core/index.ts'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MedicationActivity } from '#src/components/medications/MedicationActivity.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { useConfirmationStore } from '#src/hooks/useConfirmationStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { toast } from '#src/utils/toast.ts'

export function MedicationDetailPage({
  medicationId: propMedicationId,
  hideHeader = false,
}: {
  medicationId?: string
  hideHeader?: boolean
} = {}): JSX.Element {
  const { medicationId: paramMedicationId } = useParams<{
    medicationId: string
  }>()
  const medicationId = propMedicationId ?? paramMedicationId

  const { data: medicationRequest, isLoading } = useRead(
    ExtendedMedicationRequest,
    medicationId
  )

  const { data: allMedications = [] } = useSearch(
    ExtendedMedicationRequest,
    medicationRequest?.subject
      ? {
          patient: medicationRequest.subject,
          _sort: '-_lastUpdated',
          _count: 1000,
          'status:not': ['entered-in-error', 'draft'],
        }
      : undefined
  )

  const groupByElationThread = useFeatureFlag(
    featureFlags.medicationGroupByElationThread
  )

  const groupedMedications = useMemo((): ExtendedMedicationRequest[] => {
    if (!medicationRequest || allMedications.length === 0) {
      return []
    }

    const groups = ExtendedMedicationRequest.groupMedications(allMedications, {
      groupByElationThread,
    })
    const currentGroup = groups.find((group: MedicationGroup) =>
      group.requests.some(
        (req: ExtendedMedicationRequest) => req.id === medicationRequest.id
      )
    )

    if (!currentGroup) {
      return [medicationRequest]
    }

    return orderBy(
      currentGroup.requests,
      (request) => request.authoredOn ?? '',
      ['desc']
    )
  }, [medicationRequest, allMedications, groupByElationThread])

  const patchMutation = usePatchMutation<ExtendedMedicationRequest>()
  const openConfirmation = useConfirmationStore((state) => state.open)

  const hasUnresolvedPharmacyTransmissionIssue =
    medicationRequest?.pharmacyTransmissionIssue &&
    !medicationRequest.pharmacyTransmissionIssueResolvedAt

  const handleResolvePharmacyTransmissionIssue = (): void => {
    if (!medicationRequest) {
      return
    }

    openConfirmation({
      title: 'Resolve Pharmacy Transmission Issue?',
      description:
        'Have you properly reconciled this medication in the external system(s) (e.g. pharmacy)?',
      onConfirm: () => {
        patchMutation.mutate(
          {
            instance: medicationRequest,
            mutateFn: (instance) => {
              instance.pharmacyTransmissionIssueResolvedAt =
                new Date().toISOString()
            },
          },
          {
            onSuccess: () => {
              toast.success('Pharmacy transmission issue resolved')
            },
            onError: (error) => {
              toast.error(
                `Failed to resolve pharmacy transmission issue: ${
                  error instanceof Error ? error.message : 'Unknown error'
                }`
              )
            },
          }
        )
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader />
      </div>
    )
  }

  if (!medicationRequest) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <h2 className="mb-2 text-lg font-semibold">Medication Not Found</h2>
          <p className="text-muted-foreground">
            The requested medication could not be found.
          </p>
          <Button asChild className="mt-4">
            <Link to="../medications">Back to Medications</Link>
          </Button>
        </div>
      </div>
    )
  }

  const refillsAuthorized = medicationRequest.refillsAuthorized
  const refillsRemaining = medicationRequest.refillsRemaining
  // Surface refills whenever GraneRx has reported an authorized count, including 0
  // (see MedicationsTable comment).
  const showRefills = refillsAuthorized !== undefined
  const showDispenseSection =
    showRefills || !!medicationRequest.dispenseRequest?.quantity

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="flex items-center justify-between gap-4">
          <TypographyPageTitle>
            {medicationRequest.medicationDisplay ?? 'Unknown Medication'}
          </TypographyPageTitle>
          <div className="flex items-center gap-2">
            {hasUnresolvedPharmacyTransmissionIssue && (
              <Button
                variant="outline"
                onClick={handleResolvePharmacyTransmissionIssue}
                disabled={patchMutation.isPending}
                className="gap-2"
                size="sm"
              >
                <IconAlertTriangle className="size-4" />
                Resolve Pharmacy Transmission Issue
              </Button>
            )}
            <MoreDropdownMenu>
              <DropdownMenuMedplumLinkItem to={medicationRequest.reference} />
            </MoreDropdownMenu>
          </div>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-lg border p-4">
            <h3 className="mb-3 text-lg font-semibold">Basic Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-muted-foreground">
                  Status:
                </span>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    color={effectiveStatusBadgeColor(
                      medicationRequest.effectiveStatus
                    )}
                  >
                    {medicationRequest.effectiveStatus.label}
                  </Badge>
                </div>
              </div>

              {medicationRequest.requesterDisplay && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">
                    Ordering Provider:
                  </span>
                  <span>{medicationRequest.requesterDisplay}</span>
                </div>
              )}

              {medicationRequest.authoredOn && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">
                    Recorded:
                  </span>
                  <span>{formatDateNumber(medicationRequest.authoredOn)}</span>
                </div>
              )}

              {medicationRequest.startDate && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">
                    Start Date:
                  </span>
                  <span>{formatDateNumber(medicationRequest.startDate)}</span>
                </div>
              )}

              {medicationRequest.stopDate && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">
                    Stop Date:
                  </span>
                  <span>{formatDateNumber(medicationRequest.stopDate)}</span>
                </div>
              )}

              {medicationRequest.priority && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">
                    Priority:
                  </span>
                  <span>{medicationRequest.priority}</span>
                </div>
              )}

              {medicationRequest.intent && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">
                    Intent:
                  </span>
                  <span>{medicationRequest.intent}</span>
                </div>
              )}
            </div>
          </div>

          {medicationRequest.dosageInstructions && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-lg font-semibold">
                Dosage Instructions
              </h3>
              <div className="space-y-1">
                {uniq(medicationRequest.dosageInstructions).map(
                  (instruction) => (
                    <div key={instruction} className="text-sm">
                      {instruction}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {showDispenseSection && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-lg font-semibold">
                Dispense Information
              </h3>
              <div className="space-y-2">
                {showRefills && (
                  <div className="flex justify-between">
                    <span className="font-medium text-muted-foreground">
                      Refills left:
                    </span>
                    <span>
                      {refillsRemaining ?? 0} of {refillsAuthorized}
                    </span>
                  </div>
                )}

                {medicationRequest.dispenseRequest?.quantity && (
                  <div className="flex justify-between">
                    <span className="font-medium text-muted-foreground">
                      Quantity:
                    </span>
                    <span>
                      {medicationRequest.dispenseRequest.quantity.value}{' '}
                      {medicationRequest.dispenseRequest.quantity.unit}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {medicationRequest.noteSummary && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-lg font-semibold">Notes</h3>
              <p className="text-sm text-muted-foreground">
                {medicationRequest.noteSummary}
              </p>
            </div>
          )}

          {medicationRequest.statusReason && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-lg font-semibold">Status Reason</h3>
              <p className="text-sm text-muted-foreground">
                {medicationRequest.statusReason}
              </p>
            </div>
          )}

          {medicationRequest.reasonCode &&
            medicationRequest.reasonCode.length > 0 && (
              <div className="rounded-lg border p-4">
                <h3 className="mb-3 text-lg font-semibold">Reason Codes</h3>
                <div className="space-y-1">
                  {medicationRequest.reasonCode.map((reason) => {
                    const reasonText =
                      reason.text ??
                      reason.coding?.[0]?.display ??
                      'Unknown reason'

                    return (
                      <div key={reasonText} className="text-sm">
                        {reasonText}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

          {medicationRequest.type && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-lg font-semibold">Type</h3>
              <p className="text-sm text-muted-foreground">
                {medicationRequest.type}
              </p>
            </div>
          )}
        </div>
      </div>

      {isOfType(medicationRequest.subject, 'Patient') && (
        <Activity
          resource={medicationRequest}
          subject={medicationRequest.subject}
        >
          <MedicationActivity
            medicationRequest={medicationRequest}
            groupedMedications={groupedMedications}
          />
        </Activity>
      )}
    </div>
  )
}

type EffectiveStatus = ExtendedMedicationRequest['effectiveStatus']

function effectiveStatusBadgeColor({
  status,
  overridden,
}: EffectiveStatus): BadgeProps['color'] {
  if (overridden) {
    return 'yellow'
  }
  if (status === 'active') {
    return 'green'
  }
  if (status === 'stopped' || status === 'cancelled') {
    return 'red'
  }
  return 'gray'
}
